"use client";

import Card, { ImageSize } from "@components/Card";
import IvTeaserImage from "@components/Medias/Image/IvTeaserImage";
import { BlockSize } from "@components/cFContentArea";
import { getInternalOrExternalIcon } from "@utils/linkIconHelper";
import { getBackgroundColor } from "@utils/pageHelpers/getBackgrundColor";
import { columnSize } from "@utils/sectionHelper";
import {
  getDirection,
  getReverse,
  getTeaserImageSize,
  shouldRemoveBorder,
  shouldRemovePadding,
  shouldUseNotebookSmallImages,
  shouldUseSmallImages,
} from "@utils/teaserHelper/teaserHelper";
import { useRef } from "react";

import useMediaQuery from "hooks/useMediaQuery";
import { Context } from "types/context/Context";

import { TeaserBlockType_teaserBlock } from "./queries/contentfulTypes/TeaserBlockType";

interface TeaserBlockData extends TeaserBlockType_teaserBlock {
  teaserBlockUrl: string;
  fileSize: string;
  fileName: string;
  fileExtension: string;
  id: string;
}
interface ITeaserBlockProps {
  blockData: TeaserBlockData | any;
  uniqueKey?: string;
  context: Context;
  maxWidth: number;
}

export const TeaserBlock = ({ blockData: teaserBlock, context, maxWidth }: ITeaserBlockProps) => {
  const url = teaserBlock.teaserBlockUrl;
  const contentTypeName = teaserBlock?.link?.content?.__typename;
  const sectionHeading = context?.sectionBlock?.sectionBlockHeading ?? "";
  const gaLinkType =
    contentTypeName === "Newspage" ||
    contentTypeName === "Eventpage" ||
    /(aktuellt|senaste\s*nytt)/i.test(sectionHeading)
      ? ("news" as const)
      : ("teaser" as const);
  const linkType = getInternalOrExternalIcon(
    teaserBlock?.externalLink,
    teaserBlock?.link,
    teaserBlock?.globalLinkItem,
  );
  const cardRef = useRef(null);

  const headingText =
    context?.sectionBlock && context?.sectionBlock?.noHeading ? null : teaserBlock?.heading;
  const text = context?.sectionBlock && context?.sectionBlock?.noText ? null : teaserBlock?.text;
  const linkText =
    context?.sectionBlock && context?.sectionBlock?.noLink ? null : teaserBlock?.linkText;

  const isMobile = useMediaQuery("mobile");
  const isTabletOnly = useMediaQuery("tabletOnly");
  const isNotebook = useMediaQuery("notebook");
  const imageData = teaserBlock?.ivImage?.[0];
  const getImage = (imageData: any) => {
    return context?.sectionBlock && context?.sectionBlock?.noImage ? null : (
      <IvTeaserImage image={imageData} />
    );
  };

  let size: number;
  const contextSize = context?.columns || 1;
  if (context?.sectionBlock) {
    size = contextSize < 2 || isMobile ? 12 : contextSize === 2 ? 6 : 4;
  } else {
    size = columnSize(contextSize);
  }

  const useSmallImage = shouldUseSmallImages(size, maxWidth, context);
  const reverse = getReverse(size, maxWidth) && !teaserBlock?.isReverse;
  const direction = getDirection(size, maxWidth, !isNotebook && isTabletOnly);
  const imgSize: ImageSize = getTeaserImageSize(size, maxWidth, useSmallImage, context);
  const useNotebookSmallImages = shouldUseNotebookSmallImages(size, maxWidth);
  const noPadding = shouldRemovePadding(teaserBlock?.backgroundColor, context);

  return (
    <>
      <Card
        // onCardClick={handleClick}
        linkUrl={url}
        gaLinkType={gaLinkType}
        nextImage={getImage(imageData)}
        direction={direction}
        heading={headingText}
        headingVariant={context?.heading?.parentLevel}
        text={text}
        textList={teaserBlock?.listedItems}
        linkText={linkText}
        reverse={reverse}
        linkType={linkType}
        data-blockname={context?.relatedContent ? "relaterat innehåll" : "puff"}
        dataTestId="teaser-block"
        removeBorder={shouldRemoveBorder(context, teaserBlock?.backgroundColor)}
        backgroundColor={getBackgroundColor(teaserBlock?.backgroundColor)}
        linkVariant={teaserBlock?.useButtonLink ? "button" : "link"}
        imgSize={imgSize}
        fileExtension={teaserBlock?.fileExtension}
        notebookSmallImage={useNotebookSmallImages}
        removePadding={noPadding}
        isCampaign={teaserBlock?.isCampaignTeaser}
        cardRef={cardRef}
        className={
          context?.sizes?.desktop === BlockSize.Full || context?.sizes?.notebook === BlockSize.Full
            ? "full-width"
            : ""
        }
        context={context}
        data-contentful-entry-id={teaserBlock?.sys?.id}
        data-contentful-field-id="name"
      />
    </>
  );
};

export default TeaserBlock;
